(() => {
  // utils/components/cart-performance.ts
  class CartPerformance {
    static #metric_prefix = "cart-performance";
    static createStartingMarker(benchmarkName) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      return performance.mark(`${metricName}:start`);
    }
    static measureFromEvent(benchmarkName, event) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const startMarker = performance.mark(`${metricName}:start`, {
        startTime: event.timeStamp,
      });
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(
        metricName,
        `${metricName}:start`,
        `${metricName}:end`,
      );
    }
    static measureFromMarker(benchmarkName, startMarker) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(metricName, startMarker.name, `${metricName}:end`);
    }
    static measure(benchmarkName, callback) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const startMarker = performance.mark(`${metricName}:start`);
      callback();
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(
        metricName,
        `${metricName}:start`,
        `${metricName}:end`,
      );
    }
  }
  // utils/getFocusableElements.ts
  var getFocusableElements = (container) => {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
      ),
    );
  };

  // utils/trapFocus.ts
  var trapFocusHandlers = {};
  var trapFocus = (container, elementToFocus = container) => {
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];
    removeTrapFocus();
    trapFocusHandlers.focusin = (event) => {
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
        return;
      if (trapFocusHandlers.keydown) {
        document.addEventListener("keydown", trapFocusHandlers.keydown);
      }
    };
    trapFocusHandlers.focusout = () => {
      if (trapFocusHandlers.keydown) {
        document.removeEventListener("keydown", trapFocusHandlers.keydown);
      }
    };
    trapFocusHandlers.keydown = (event) => {
      if (event.code.toUpperCase() !== "TAB") return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };
    document.addEventListener("focusout", trapFocusHandlers.focusout);
    document.addEventListener("focusin", trapFocusHandlers.focusin);
    elementToFocus.focus();
    if (elementToFocus instanceof HTMLInputElement) {
      if (
        ["search", "text", "email", "url"].includes(elementToFocus.type) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    }
  };
  var removeTrapFocus = (elementToFocus = null) => {
    if (trapFocusHandlers.focusin) {
      document.removeEventListener("focusin", trapFocusHandlers.focusin);
    }
    if (trapFocusHandlers.focusout) {
      document.removeEventListener("focusout", trapFocusHandlers.focusout);
    }
    if (trapFocusHandlers.keydown) {
      document.removeEventListener("keydown", trapFocusHandlers.keydown);
    }
    if (elementToFocus) elementToFocus.focus();
  };

  // utils/components/menu-drawer.ts
  class MenuDrawer extends HTMLElement {
    mainDetailsToggle;
    constructor() {
      super();
      this.mainDetailsToggle = this.querySelector("details");
      this.addEventListener("keyup", this.onKeyUp.bind(this));
      this.addEventListener("focusout", this.onFocusOut.bind(this));
      this.bindEvents();
    }
    bindEvents() {
      this.querySelectorAll("summary").forEach((summary) =>
        summary.addEventListener("click", this.onSummaryClick.bind(this)),
      );
      this.querySelectorAll(
        "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
      ).forEach((button) =>
        button.addEventListener("click", this.onCloseButtonClick.bind(this)),
      );
    }
    onKeyUp(event) {
      if (event.code.toUpperCase() !== "ESCAPE") return;
      const openDetailsElement = event.target.closest("details[open]");
      if (!openDetailsElement) return;
      if (openDetailsElement === this.mainDetailsToggle) {
        this.closeMenuDrawer(
          event,
          this.mainDetailsToggle?.querySelector("summary") ?? null,
        );
      } else {
        this.closeSubmenu(openDetailsElement);
      }
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest(".has-submenu");
      const isOpen = detailsElement.hasAttribute("open");
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      function addTrapFocus() {
        const nextElement = summaryElement.nextElementSibling;
        const button = detailsElement.querySelector("button");
        if (button) {
          trapFocus(nextElement, button);
        } else {
          trapFocus(nextElement);
        }
        summaryElement.nextElementSibling?.removeEventListener(
          "transitionend",
          addTrapFocus,
        );
      }
      if (detailsElement === this.mainDetailsToggle) {
        if (isOpen) event.preventDefault();
        isOpen
          ? this.closeMenuDrawer(event, summaryElement)
          : this.openMenuDrawer(summaryElement);
        if (window.matchMedia("(max-width: 1024px)")) {
          document.documentElement.style.setProperty(
            "--viewport-height",
            `${window.innerHeight}px`,
          );
        }
      } else {
        setTimeout(() => {
          detailsElement.classList.add("menu-opening");
          summaryElement.setAttribute("aria-expanded", "true");
          parentMenuElement && parentMenuElement.classList.add("submenu-open");
          !reducedMotion || reducedMotion.matches
            ? addTrapFocus()
            : summaryElement.nextElementSibling?.addEventListener(
                "transitionend",
                addTrapFocus,
              );
        }, 100);
      }
    }
    openMenuDrawer(summaryElement) {
      setTimeout(() => {
        this.mainDetailsToggle?.classList.add("menu-opening");
      });
      summaryElement.setAttribute("aria-expanded", "true");
      if (this.mainDetailsToggle) {
        trapFocus(this.mainDetailsToggle, summaryElement);
      }
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }
    closeMenuDrawer(event, elementToFocus = null) {
      if (event === undefined) return;
      this.mainDetailsToggle?.classList.remove("menu-opening");
      this.mainDetailsToggle?.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      this.mainDetailsToggle
        ?.querySelectorAll(".submenu-open")
        .forEach((submenu) => {
          submenu.classList.remove("submenu-open");
        });
      document.body.classList.remove(
        `overflow-hidden-${this.dataset.breakpoint}`,
      );
      removeTrapFocus(elementToFocus);
      if (this.mainDetailsToggle) {
        this.closeAnimation(this.mainDetailsToggle);
      }
      if (event instanceof KeyboardEvent)
        elementToFocus?.setAttribute("aria-expanded", "false");
    }
    onFocusOut() {
      setTimeout(() => {
        if (
          this.mainDetailsToggle?.hasAttribute("open") &&
          !this.mainDetailsToggle?.contains(document.activeElement)
        )
          this.closeMenuDrawer();
      });
    }
    onCloseButtonClick(event) {
      const detailsElement = event.currentTarget.closest("details");
      if (detailsElement) {
        this.closeSubmenu(detailsElement);
      }
    }
    closeSubmenu(detailsElement) {
      const parentMenuElement = detailsElement.closest(".submenu-open");
      parentMenuElement && parentMenuElement.classList.remove("submenu-open");
      detailsElement.classList.remove("menu-opening");
      detailsElement
        .querySelector("summary")
        ?.setAttribute("aria-expanded", "false");
      removeTrapFocus(detailsElement.querySelector("summary"));
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;
      detailsElement.setAttribute("data-closing", "true");
      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }
        const elapsedTime = time - animationStart;
        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute("open");
          detailsElement.removeAttribute("data-closing");
          const openDetails = detailsElement.closest("details[open]");
          if (openDetails) {
            const summary = detailsElement.querySelector("summary");
            if (summary) {
              trapFocus(openDetails, summary);
            } else {
              trapFocus(openDetails);
            }
          }
        }
      };
      window.requestAnimationFrame(handleAnimation);
    }
  }
  // utils/modalOverflow.ts
  var hasOpenModals = () => {
    const openMegaMenus = document.querySelectorAll(
      "header-mega-menu-content[open]",
    );
    if (openMegaMenus.length > 0) return true;
    const openSearchModal = document.querySelector("search-modal[open]");
    if (openSearchModal) return true;
    const openCartDrawer = document.querySelector(".em-cd.is-open");
    if (openCartDrawer) return true;
    return false;
  };
  var setBodyOverflow = (value) => {
    if (value === "hidden") {
      document.body.style.overflow = "hidden";
    } else {
      if (!hasOpenModals()) {
        document.body.style.overflow = "";
      }
    }
  };

  // assets/scripts/header/header-drawer.ts
  class HeaderDrawer extends MenuDrawer {
    header;
    borderOffset;
    constructor() {
      super();
    }
    openMenuDrawer(summaryElement) {
      this.header = this.header || document.querySelector(".section-header");
      this.borderOffset =
        this.borderOffset ||
        this.closest(".header-wrapper")?.classList.contains(
          "header-wrapper--border-bottom",
        )
          ? 1
          : 0;
      if (this.header) {
        document.documentElement.style.setProperty(
          "--header-bottom-position",
          `${Number.parseInt(String(this.header.getBoundingClientRect().bottom - (this.borderOffset ?? 0)))}px`,
        );
        this.header.classList.add("menu-open");
      }
      setTimeout(() => {
        this.mainDetailsToggle?.classList.add("menu-opening");
      });
      summaryElement.setAttribute("aria-expanded", "true");
      window.addEventListener("resize", this.onResize);
      if (this.mainDetailsToggle) {
        trapFocus(this.mainDetailsToggle, summaryElement);
      }
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
      setBodyOverflow("hidden");
    }
    closeMenuDrawer(event, elementToFocus = null) {
      if (!elementToFocus) return;
      super.closeMenuDrawer(event, elementToFocus);
      this.header?.classList.remove("menu-open");
      setBodyOverflow("");
      window.removeEventListener("resize", this.onResize);
    }
    onResize = () => {
      if (this.header) {
        document.documentElement.style.setProperty(
          "--header-bottom-position",
          `${Number.parseInt(String(this.header.getBoundingClientRect().bottom - (this.borderOffset ?? 0)))}px`,
        );
      }
      document.documentElement.style.setProperty(
        "--viewport-height",
        `${window.innerHeight}px`,
      );
    };
  }
  if (!customElements.get("header-drawer")) {
    customElements.define("header-drawer", HeaderDrawer);
  }
})();
